import { useState, useEffect } from 'react';
import { useAuth, useBooking } from '../../context/contexts';
import { PaymentForm, CreditCard, ApplePay, GooglePay } from 'react-square-web-payments-sdk';
import { useNavigate } from 'react-router-dom';
import { UserBooking } from '../../context/UserBookingContext';
import { toast } from 'sonner';
import './Payments.css';

const CustomerForm = ({ formData, handleInputChange, currentUser, onNext, location }) => {
    return (
        <div className="step-container">
            <h4>Please fill out the information below</h4>
            {!currentUser && (
                <>
                    <div className="form-fullname">
                        <div className="form-group">
                            <input
                                type="text"
                                name="firstName"
                                placeholder="First Name *"
                                required
                                value={formData.firstName}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                name="lastName"
                                placeholder="Last Name"
                                value={formData.lastName}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <input
                            type="tel"
                            name="phone"
                            placeholder="Phone Number *"
                            required
                            value={formData.phone}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="email"
                            name="email"
                            placeholder="Email *"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                    </div>
                </>
            )}
            {location === 'atHome' && (
                <div className="form-group">
                    <input
                        type="text"
                        name="address"
                        placeholder="Address for a mobile detailing service *"
                        required
                        value={formData.address}
                        onChange={handleInputChange}
                        className="address-input"
                    />
                </div>
            )}
            <button className="next-btn" onClick={onNext}>
                Continue to Payment
            </button>
        </div>
    );
};

const PaymentStep = ({ paymentMethod, setPaymentMethod, handlePayment, onBack, totalPrice, isSubmitting }) => {
    return (
        <div className="step-container">
            <button className="back-button" onClick={onBack}>
                ← Back
            </button>
            <h3>Payment Method</h3>
            <div className="payment-summary">
                <h4>Total Amount</h4>
                <div className="total-amount">${totalPrice.toFixed(2)}</div>
            </div>
            <div className="payment-options">
                {/* <button 
                    type="button" 
                    className={`payment-btn ${paymentMethod === "payNow" ? 'selected' : ''}`}
                    onClick={() => setPaymentMethod("payNow")}
                    disabled={isSubmitting}
                >
                    Pay Now
                </button> */}
                <button 
                    type="button" 
                    className={`payment-btn ${paymentMethod === "inShop" ? 'selected' : ''}`}
                    onClick={() => {
                        setPaymentMethod("inShop");
                        // Automatically process the booking when "Pay In Shop" is selected
                        if (paymentMethod !== "inShop") {
                            setTimeout(() => handlePayment(), 100);
                        }
                    }}
                    disabled={isSubmitting}
                >
                    Pay In Shop
                </button>
            </div>

            {paymentMethod === "payNow" && (
                <div className="confirmation">
                    <PaymentForm
                        applicationId={process.env.REACT_APP_SQUARE_SANDBOX_APPLICATION_ID}
                        locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                        // createPaymentRequest={ async() => ({
                        //     countryCode: 'US',
                        //     currency: 'USD',
                        //     total: {
                        //         amount: totalPrice,
                        //         label: 'Total'
                        //     },
                            
                        // })}
                        cardTokenizeResponseReceived={async (token, verifiedBuyer) => {
                            try {
                                const response = await fetch(
                                    `${process.env.REACT_APP_SUPABASE_FUNCTION_URL}/process-payment`,
                                    {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                        },
                                        body: JSON.stringify({
                                            sourceId: token.token,
                                            amount: totalPrice,
                                            currency: 'USD'
                                        }),
                                    }
                                );

                                const result = await response.json();
                                
                                if (!response.ok) {
                                    throw new Error(result.error || 'Payment failed');
                                }

                                // Handle successful payment
                                await handlePayment();
                            } catch (error) {
                                console.error('Payment error:', error);
                                alert(`Payment failed: ${error.message}`);
                            }
                        }}
                    >
                        {/* <ApplePay /> */}
                        {/* <GooglePay /> */}
                        <CreditCard 
                            buttonProps={{
                                css: {
                                    backgroundColor: "#771520",
                                    fontSize: "14px",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "#530f16",
                                    },
                                },
                            }}
                        />
                    </PaymentForm>
                </div>
            )}
            
            {isSubmitting && paymentMethod === "inShop" && (
                <div className="processing-message">
                    <p>Processing your reservation...</p>
                </div>
            )}
        </div>
    );
};

const Payments = () => {
    const navigate = useNavigate();
    const { currentUser, userFirstName, userLastName, userPhone } = useAuth();
    const { 
        selectedCar, 
        selectedService, 
        selectedAddOns, 
        totalPrice,
        createCompleteNewBooking,
        selectedDate,
        selectedTime,
        location,
        clearBooking
    } = useBooking();
    
    const [currentStep, setCurrentStep] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState("");
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: userFirstName || '',
                lastName: userLastName || '',
                phone: userPhone || '',
                email: currentUser.email || ''
            });
        }
    }, [currentUser, userFirstName, userLastName, userPhone]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handlePayment = async (e) => {
        if (e) e.preventDefault();
        
        if (isSubmitting) return; // Prevent multiple submissions
        
        if (!selectedCar || !selectedService) {
            toast.error("Please select a vehicle and service before proceeding.");
            navigate('/booking');
            return;
        }

        if (!selectedDate || !selectedTime) {
            toast.error("Please select a date and time for your appointment.");
            navigate('/booking');
            return;
        }

        // Validate form data for guest users
        if (!currentUser) {
            const requiredFields = {
                firstName: 'First Name',
                email: 'Email',
                phone: 'Phone Number'
            };

            // Add address validation for at home service
            if (location === 'atHome' && !formData.address) {
                toast.error("Please provide your address for at home service.");
                return;
            }

            const missingFields = Object.entries(requiredFields)
                .filter(([key]) => !formData[key])
                .map(([_, label]) => label);

            if (missingFields.length > 0) {
                toast.error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
                return;
            }
        }

        setIsSubmitting(true);
        const toastId = toast.loading("Processing your booking...");
        
        try {
            // Prepare booking details
            const bookingDetails = {
                // User details
                firstName: formData.firstName || userFirstName || '',
                lastName: formData.lastName || userLastName || '',
                email: formData.email || (currentUser?.email || ''),
                phone: formData.phone || (userPhone || ''),
                
                // Booking details
                vehicleTypeId: selectedCar.id,
                serviceId: selectedService.id,
                addons: selectedAddOns.map(addon => addon.id),
                date: selectedDate,
                time: selectedTime,
                totalPrice: totalPrice,
                locationService: location,
                address: formData.address || ''
            };
            // Create the booking using the context version
            const result = await createCompleteNewBooking(bookingDetails);
            
            if (!result.success) {
                throw new Error(result.error?.message || 'Failed to create booking');
            }
            
            // Send the booking confirmation email
            try {
                await fetch(
                    `${process.env.REACT_APP_SUPABASE_FUNCTION_URL}/send-booking-confirmation`, 
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            firstName: formData.firstName || currentUser?.first_name || '',
                            lastName: formData.lastName || currentUser?.last_name || '',
                            email: formData.email || (currentUser?.email || ''),
                            phone: formData.phone || (currentUser?.phone_number || ''),
                            date: selectedDate,
                            time: selectedTime,
                            vehicleType: selectedCar.title,
                            serviceName: selectedService.name,
                            addons: selectedAddOns.map(addon => addon.name),
                            totalPrice: totalPrice,
                            bookingId: result.data.id || '',
                            locationService: location,
                            address: formData.address || ''
                        }),
                    }
                );
            } catch (emailError) {
                console.error('Error sending booking confirmation email:', emailError);
            }
            
            // Check for warnings
            if (result.warnings && result.warnings.length > 0) {
                console.warn("Booking created with warnings:", result.warnings);
                toast.dismiss(toastId);
                toast.warning("Booking created, but some add-ons could not be added.");
            } else {
                toast.dismiss(toastId);
                toast.success("Your reservation has been confirmed!");
            }
            
            // Redirect to home page
            setTimeout(() => {
                navigate('/');
                // Clear booking data
                clearBooking();
            }, 2000);
            
        } catch (error) {
            console.error('Error creating booking:', error);
            toast.dismiss(toastId);
            
            // Handle specific error cases
            if (error.message?.includes('permission denied')) {
                toast.error('Unable to process booking. Please try again or contact support.');
            } else if (error.message?.includes('duplicate key')) {
                toast.error('This time slot is no longer available. Please choose another time.');
            } else if (error.message?.includes('not found')) {
                toast.error('The selected time slot is no longer available. Please choose another time.');
            } else {
                toast.error('Failed to create booking. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = () => {
        // Validate form data for guest users
        if (!currentUser) {
            if (!formData.firstName || !formData.email || !formData.phone) {
                toast.error("Please fill in all required fields.");
                return;
            }
            // Add address validation for at home service
            if (location === 'atHome' && !formData.address) {
                toast.error("Please provide your address for at home service.");
                return;
            }
        }
        setCurrentStep(1);
    };

    const handleBack = () => {
        setCurrentStep(0);
    };

    return (
        <div className="payment-container">
            <form onSubmit={handlePayment} className="payment-form">
                {currentStep === 0 ? (
                    <CustomerForm 
                        formData={formData}
                        handleInputChange={handleInputChange}
                        currentUser={currentUser}
                        onNext={handleNext}
                        location={location}
                    />
                ) : (
                    <PaymentStep 
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        handlePayment={handlePayment}
                        onBack={handleBack}
                        totalPrice={totalPrice}
                        isSubmitting={isSubmitting}
                    />
                )}
            </form>
        </div>
    );
};

export default Payments;